/**
 * The words a room's page says about its lives: which session ran last, where the replay has got to, when a
 * question was asked, and the busiest moments' marks on the room's curve. Pure, so the tabs and the tests agree.
 */
import { labelParts } from "../../../shared/live-time";
import type { ReactionMoment } from "../../../shared/live-reactions";
import type { LiveSessionReport, LiveSessionView, VideoJobState, WebinarDetail, WebinarLive } from "../../../shared/types";

/** A session's own report page. */
export function sessionReportPath(webinarId: string, sessionId: string): string {
  return `/rooms/${webinarId}/sessions/${encodeURIComponent(sessionId)}`;
}

/** The series' session that ran most recently, or null before any has. */
export function lastRun(sessions: readonly LiveSessionView[]): LiveSessionView | null {
  return sessions.filter((s) => s.state === "ended").toSorted((a, b) => b.seq - a.seq)[0] ?? null;
}

/** "Tue 27 Oct": the short day a tab and a seg name a session by. */
export function shortDay(iso: string, tz: string): string {
  const p = labelParts(iso, tz, "short");
  return p ? `${p.weekday} ${p.day} ${p.month}` : "";
}

/** "Tuesday 27 October, 6:00 PM". */
export function longDayTime(iso: string, tz: string): string {
  const p = labelParts(iso, tz, "long");
  return p ? `${p.weekday} ${p.day} ${p.month}, ${p.time}` : "";
}

/** "Tuesday 27 October". */
export function longDay(iso: string, tz: string): string {
  const p = labelParts(iso, tz, "long");
  return p ? `${p.weekday} ${p.day} ${p.month}` : "";
}

const two = (n: number) => String(n).padStart(2, "0");

/** When in the session someone asked: "at 22 min", or "before it started". */
export function askedAt(sec: number): string {
  if (sec < 0) return "before it started";
  return `at ${Math.max(0, Math.round(sec / 60))} min`;
}

/** A playhead as a clock: "4:05", "1:02:09". */
export function clock(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}:${two(m)}:${two(s % 60)}` : `${m}:${two(s % 60)}`;
}

type ReplayStepState = "done" | "on" | "wait" | "missed";

interface ReplayStep {
  label: string;
  state: ReplayStepState;
}

/** The job states the render is still working in. */
const WORKING: ReadonlyArray<VideoJobState> = ["uploading", "probing", "transcoding", "assembling", "poster", "transcribing"];
/** The job states the talk is being listened to in: the transcript runs beside the render. */
const LISTENING: ReadonlyArray<VideoJobState> = ["transcoding", "assembling", "poster", "transcribing"];

/**
 * The replay's four steps from the real video job and the chapters. The words and the chapters run beside the
 * render, so a later step can finish first; a step that will not happen says so.
 */
export function replaySteps(live: Pick<WebinarLive, "replay">): ReplayStep[] {
  const r = live.replay;
  if (!r.recorded) {
    return [
      { label: "No recording this time", state: "missed" },
      { label: "Make it playable", state: "missed" },
      { label: "Listen to the talk", state: "missed" },
      { label: "Write the chapters", state: "missed" },
    ];
  }
  const state = r.videoState;
  const settled = r.playable || state === "error";
  // Why "ready" counts as working until it plays: the job finishes a moment before the replay's own HLS is written.
  const working = state !== null && (WORKING.includes(state) || (state === "ready" && !r.playable));
  const listening = state !== null && LISTENING.includes(state);
  return [
    { label: "Saved the recording", state: "done" },
    r.playable
      ? { label: "Made it playable", state: "done" }
      : state === "error"
        ? { label: "It didn't finish playable", state: "missed" }
        : { label: "Making it playable", state: working ? "on" : "wait" },
    r.transcript
      ? { label: "Listened to the talk", state: "done" }
      : settled
        ? { label: "We couldn't hear it this time", state: "missed" }
        : { label: "Listening to the talk", state: listening ? "on" : "wait" },
    r.chapters > 0
      ? { label: `Wrote ${r.chapters} ${r.chapters === 1 ? "chapter" : "chapters"}`, state: "done" }
      : settled
        ? { label: "No chapters this time", state: "missed" }
        : { label: "Writing the chapters", state: r.transcript ? "on" : "wait" },
  ];
}

/** The replay's pill: ready (it plays, the same check as the player's), being made, or none. */
export function replayPill(live: Pick<WebinarLive, "replay">): { label: string; tone: "good" | "warn" | "plain" } {
  if (!live.replay.recorded) return { label: "No recording", tone: "plain" };
  if (live.replay.playable) return { label: "Ready", tone: "good" };
  if (live.replay.videoState === "error") return { label: "Didn't finish", tone: "plain" };
  return { label: "Being made", tone: "warn" };
}

/** Is the replay still being made? The page asks again until it plays or stops. */
export function replayBuilding(live: Pick<WebinarLive, "state" | "replay">): boolean {
  const state = live.replay.videoState;
  if (live.state !== "ended" || !live.replay.recorded || state === null || live.replay.playable) return false;
  return WORKING.includes(state) || state === "ready";
}

/** One mark on the room's curve: the second, what was going on there, and the count the design gives it. */
export interface BusiestMark {
  at: number;
  words: string;
  /** "Most at once: 47." or "19 clicked."; empty when the moment has no count of its own. */
  extra: string;
}

/** What a reaction moment says: its chapter, else the words spoken there, else its time. */
export function markWords(m: ReactionMoment): string {
  if (m.chapter) return m.chapter;
  if (m.words) return `“${m.words}”`;
  return `The moment at ${clock(m.at)}`;
}

/**
 * The busiest moments to mark on the curve, in the order they came: the minute the room was fullest, the minute the
 * offer came up with how many pressed it from then on, then the moments most people reacted at, up to three. A
 * moment in a minute already marked adds no second mark.
 */
export function busiestMarks(
  moments: readonly ReactionMoment[],
  report: Pick<LiveSessionReport, "chapters" | "mostAtOnce" | "mostAtOnceAt" | "offer"> | undefined,
): BusiestMark[] {
  const marks: BusiestMark[] = [];
  const free = (at: number) => marks.every((m) => Math.floor(m.at / 60) !== Math.floor(at / 60));
  if (report && report.mostAtOnce > 0) {
    const at = report.mostAtOnceAt;
    const chapter = report.chapters.find((c) => c.fromSec <= at && at < c.toSec)?.name;
    marks.push({ at, words: chapter ?? `The moment at ${clock(at)}`, extra: `Most at once: ${report.mostAtOnce.toLocaleString("en-US")}.` });
  }
  if (report?.offer && free(report.offer.at)) {
    const { at, label, clicked } = report.offer;
    marks.push({ at, words: label ? `The offer: ${label}` : "The offer", extra: clicked > 0 ? `${clicked.toLocaleString("en-US")} clicked.` : "" });
  }
  const byPeople = moments.toSorted((a, b) => b.people.live + b.people.replay - (a.people.live + a.people.replay));
  for (const m of byPeople) {
    if (marks.length >= 3) break;
    if (free(m.at)) marks.push({ at: m.at, words: markWords(m), extra: "" });
  }
  return marks.toSorted((a, b) => a.at - b.at);
}

/** Why a room can read 0 with a sign-up in the list's history: the team's own tests are never counted. */
export function teamTestsLine(tests: number): string {
  return `Not counting ${tests.toLocaleString("en-US")} ${tests === 1 ? "test" : "tests"} by your team.`;
}

/** What the all-time numbers are about: "All 9 sessions since 1 September", or "Everyone so far" with the day of a one-off live. */
export function allTimeHead(detail: Pick<WebinarDetail, "live">): { title: string; sub: string } {
  const series = detail.live?.series;
  const ran = series ? series.sessions.filter((s) => s.state === "ended").length : 0;
  const first = series?.sessions.toSorted((a, b) => a.seq - b.seq).find((s) => s.state === "ended");
  const oneOff = detail.live && !series ? detail.live : null;
  const title = series ? `All ${ran.toLocaleString("en-US")} ${ran === 1 ? "session" : "sessions"}` : "Everyone so far";
  const sub = first
    ? `Since ${longDay(first.startsAt, first.timezone).split(" ").slice(1).join(" ")}`
    : oneOff
      ? longDay(oneOff.startsAt, oneOff.timezone)
      : "";
  return { title, sub };
}
